import { Box, Flex, Text, SimpleGrid, Heading, Card, SimpleGrid as Grid, IconButton } from "@chakra-ui/react";
import { useState } from "react";
import { colors } from "../../theme";
import {
  MallIcon,
  RestaurantIcon,
  UsersIcon,
  OrdersIcon,
} from "../../utils/svg";
import { formatCount, formatOMR } from "../../utils/format";
import { useGetAnalyticsData, useMalls } from "../../services/useQueries";
import { useAdminRole } from "../../utils/useAdminRole";
import MallRevenueCard from "./MallRevenueCard";

// A headline figure, with a line saying what it counts.
const StatCard = ({ title, value, note, icon: Icon, iconBg }) => (
  <Card.Root bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" margin={1}>
    <Card.Body p={6}>
      <Flex justify="space-between" align="flex-start">
        <Box>
          <Text fontSize="sm" color={colors.textSecondary} mb={1}>
            {title}
          </Text>
          <Heading size="xl" color={colors.text} mb={2}>
            {value}
          </Heading>
          <Text fontSize="sm" color={colors.textSecondary}>
            {note}
          </Text>
        </Box>
        <Flex
          w={12}
          h={12}
          borderRadius="lg"
          bg={iconBg}
          align="center"
          justify="center"
        >
          <Icon boxSize={6} color="white" />
        </Flex>
      </Flex>
    </Card.Body>
  </Card.Root>
);



const DashboardScreen = () => {
  // Map API analytics data into stats for the UI. For MALL_ADMIN the backend
  // scopes /analytics/overall to their mall (GAP-018) — same response shape.
  const { data: analytics } = useGetAnalyticsData();
  // All malls system-wide. useMalls handles undefined filter -> /malls/mall/get-all.
  const { data: allMallsRes, isLoading: mallsLoading } = useMalls();
  const { isMallAdmin, managedMallId } = useAdminRole();
  const fetchedMalls = Array.isArray(allMallsRes)
    ? allMallsRes
    : allMallsRes?.data || allMallsRes?.items || [];
  // A mall admin's dashboard shows only their own mall's revenue card.
  const allMalls = isMallAdmin
    ? fetchedMalls.filter((mall) => mall.id === managedMallId)
    : fetchedMalls;
  const analyticsData = analytics?.data || {};

  // Revenue-per-mall carousel — show 2 mall cards per page.
  const PAGE_SIZE = 2;
  const [mallPage, setMallPage] = useState(0);
  const mallPageCount = Math.max(1, Math.ceil(allMalls.length / PAGE_SIZE));
  const visibleMalls = allMalls.slice(
    mallPage * PAGE_SIZE,
    mallPage * PAGE_SIZE + PAGE_SIZE
  );
  const stats = [
    {
      title: "Customers",
      value: formatCount(analyticsData?.totalCustomers),
      note: isMallAdmin ? "Buyer accounts that chose this mall" : "Buyer accounts",
      icon: UsersIcon,
      iconBg: colors.primary,
    },
    {
      title: "Shops",
      value: formatCount(analyticsData?.totalRestaurants),
      note: "Including ones waiting for approval",
      icon: RestaurantIcon,
      iconBg: "#22C55E",
    },
    {
      title: "Orders placed",
      value: formatCount(analyticsData?.totalOrders),
      note: `${formatCount(analyticsData?.deliveredOrders)} delivered · ${formatCount(
        analyticsData?.cancelledOrders
      )} cancelled · ${formatCount(analyticsData?.inProgressOrders)} open`,
      icon: OrdersIcon,
      iconBg: "#F59E0B",
    },
    {
      title: "Revenue",
      value: formatOMR(analyticsData?.totalRevenue),
      note: "From delivered orders, all time",
      icon: MallIcon,
      iconBg: "#3B82F6",
    },
  ];



  return (
    <Box>
      {/* Page Header */}
      <Box mb={6}>
        <Heading size="lg" color={colors.text} mb={1}>
          Dashboard
        </Heading>
        <Text color={colors.textSecondary}>
          Figures from orders placed in the app. Revenue counts delivered orders only.
        </Text>
      </Box>

      {/* Stats Grid */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} mb={8}>
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </SimpleGrid>

      {/* Revenue per Mall — 2 cards per page, carousel arrows when overflow */}
      <Box
        bg="white"
        borderRadius="lg"
        boxShadow="sm"
        p={5}
        borderWidth="1px"
        borderColor="gray.100"
      >
        <Flex justify="space-between" align="center" mb={5}>
          <Heading size="md" color={colors.text}>
            By mall
          </Heading>
          {mallPageCount > 1 && (
            <Flex gap={2}>
              <IconButton
                size="sm"
                variant="outline"
                borderRadius="full"
                aria-label="Previous"
                onClick={() => setMallPage((p) => Math.max(0, p - 1))}
                disabled={mallPage === 0}
              >
                ‹
              </IconButton>
              <IconButton
                size="sm"
                variant="outline"
                borderRadius="full"
                aria-label="Next"
                onClick={() =>
                  setMallPage((p) => Math.min(mallPageCount - 1, p + 1))
                }
                disabled={mallPage >= mallPageCount - 1}
              >
                ›
              </IconButton>
            </Flex>
          )}
        </Flex>

        {mallsLoading ? (
          <Box py={10} textAlign="center">
            <Text color={colors.textSecondary}>Loading malls…</Text>
          </Box>
        ) : allMalls.length === 0 ? (
          <Box py={10} textAlign="center">
            <Text color={colors.textSecondary}>No malls available.</Text>
          </Box>
        ) : (
          <Grid columns={{ base: 1, lg: 2 }} gap={5}>
            {visibleMalls.map((mall) => (
              <MallRevenueCard key={mall.id} mall={mall} />
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default DashboardScreen;
